import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { LifeBuoy, Send, ExternalLink, Inbox, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useGuild } from '@/contexts/GuildContext';
import {
  API, authHeader, inputCls, labelCls, cardCls, btnPrimary, timeAgo, PageShell, PageLoading, EmptyState,
} from '@/lib/pageKit';

const KINDS = [
  { value: 'bug', label: '🐞 Bug report' },
  { value: 'suggestion', label: '💡 Suggestion' },
  { value: 'contact', label: '✉️ Contact the developer' },
];

function Thread({ ticket, onSent, isOwnerView }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);

  const send = async (close = false) => {
    if (!text.trim() && !close) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/support/tickets/${ticket.id}/reply`,
        { message: text.trim() || 'Ticket closed.', close }, authHeader());
      setText('');
      onSent();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`${cardCls} p-5`} data-testid={`support-ticket-${ticket.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <p className="text-[var(--text-primary)] font-semibold font-[Outfit] truncate">{ticket.subject}</p>
          <p className="text-xs text-[var(--text-muted)] font-[Manrope]">
            {KINDS.find(k => k.value === ticket.kind)?.label} · {ticket.guild_name}
            {isOwnerView && ` · ${ticket.username}`} · {timeAgo(ticket.updated_at)}
          </p>
        </div>
        <span className={`text-xs px-2 py-1 rounded-lg font-[Manrope] shrink-0 ${ticket.status === 'closed'
          ? 'bg-emerald-500/10 text-emerald-400' : 'bg-[#5865F2]/15 text-[#8c97ff]'}`}>
          {ticket.status === 'closed' ? 'Closed' : ticket.last_from === 'dev' ? 'Answered' : 'Open'}
        </span>
      </div>
      <div className="space-y-2 mb-3 max-h-72 overflow-y-auto">
        {ticket.messages.map((m, i) => (
          <div key={i} className={`rounded-xl px-3 py-2 text-sm font-[Manrope] ${m.from === 'dev'
            ? 'bg-[#5865F2]/10 border border-[#5865F2]/20' : 'bg-[var(--bg-hover)] border border-[var(--border)]'}`}>
            <p className="text-xs text-[var(--text-muted)] mb-0.5">{m.from === 'dev' ? '🛠️ Developer' : m.name} · {timeAgo(m.at)}</p>
            <p className="text-[var(--text-primary)] whitespace-pre-wrap break-words">{m.text}</p>
          </div>
        ))}
      </div>
      {(ticket.status !== 'closed' || isOwnerView) && (
        <div className="flex gap-2 items-end">
          <textarea value={text} onChange={e => setText(e.target.value)} rows={2} maxLength={1500}
            placeholder={isOwnerView ? 'Reply (sent to their DMs too)…' : 'Add a reply…'} className={`${inputCls} resize-none`} />
          <div className="flex flex-col gap-2">
            <button onClick={() => send(false)} disabled={busy || !text.trim()} className={btnPrimary}>
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send
            </button>
            {isOwnerView && ticket.status !== 'closed' && (
              <button onClick={() => send(true)} disabled={busy}
                className="px-3 py-2 rounded-xl text-sm font-[Manrope] text-emerald-400 hover:bg-emerald-500/10 border border-[var(--border)]">
                Close
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Support() {
  const { user } = useAuth();
  const { selectedGuild, selectedGuildId } = useGuild();
  const isGlobal = !!selectedGuild?.is_global;
  const [botInfo, setBotInfo] = useState(null);
  const [tickets, setTickets] = useState(null);
  const [inbox, setInbox] = useState(null);
  const [tab, setTab] = useState('mine');
  const [form, setForm] = useState({ kind: 'bug', subject: '', details: '' });
  const [sending, setSending] = useState(false);

  const isOwner = !!(botInfo?.owner_id && user?.user_id && String(user.user_id) === String(botInfo.owner_id));
  const canCreate = !!selectedGuild?.is_staff && !isGlobal;

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/support/tickets`, authHeader());
      setTickets(r.data.tickets || []);
    } catch { setTickets([]); }
    if (isOwner) {
      try {
        const r = await axios.get(`${API}/api/support/admin/tickets?status=all`, authHeader());
        setInbox(r.data.tickets || []);
      } catch { setInbox([]); }
    }
  }, [isOwner]);

  useEffect(() => { axios.get(`${API}/api/bot/status`).then(r => setBotInfo(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const submit = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/api/support/tickets`, { guild_id: selectedGuildId, ...form }, authHeader());
      toast.success('Ticket sent to the developer');
      setForm({ kind: 'bug', subject: '', details: '' });
      setTab('mine');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send ticket');
    } finally {
      setSending(false);
    }
  };

  if (tickets === null) return <PageLoading />;
  const list = tab === 'inbox' ? inbox || [] : tickets;

  return (
    <PageShell icon={LifeBuoy} title="Support" subtitle="Report a bug, suggest a feature or contact the developer"
      testId="support-main" maxW="max-w-3xl"
      actions={botInfo?.support_url && (
        <a href={botInfo.support_url} target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-2 px-4 py-2 rounded-xl border border-[var(--border)] text-sm font-[Manrope] text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
          <ExternalLink size={14} /> Support server
        </a>
      )}>
      <div className={`${cardCls} p-5 space-y-4`}>
        <h2 className="text-base font-semibold font-[Outfit] text-[var(--text-primary)]">New ticket</h2>
        {!canCreate ? (
          <p className="text-sm text-[var(--text-muted)] font-[Manrope]">
            {isGlobal ? 'Select a specific server to open a ticket.' : 'Only server admins (Manage Server) can open tickets with the developer.'}
          </p>
        ) : (
          <>
            <div>
              <label className={labelCls}>Type</label>
              <select value={form.kind} onChange={e => setForm(f => ({ ...f, kind: e.target.value }))} className={inputCls}>
                {KINDS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Subject</label>
              <input value={form.subject} maxLength={100} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Details</label>
              <textarea value={form.details} rows={5} maxLength={1500} onChange={e => setForm(f => ({ ...f, details: e.target.value }))}
                placeholder="What happened? Include the command or page and what you expected." className={`${inputCls} resize-none`} />
            </div>
            <button onClick={submit} disabled={sending || !form.subject.trim() || !form.details.trim()} className={btnPrimary}>
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send ticket
            </button>
          </>
        )}
      </div>

      <div className="flex gap-2">
        {[['mine', 'My tickets', tickets.length], ...(isOwner ? [['inbox', 'Developer inbox', (inbox || []).filter(t => t.status === 'open').length]] : [])].map(([key, label, n]) => (
          <button key={key} onClick={() => setTab(key)}
            className={`px-4 py-2 rounded-xl text-sm font-[Manrope] border ${tab === key
              ? 'bg-[#5865F2]/15 border-[#5865F2]/40 text-[var(--text-primary)]' : 'border-[var(--border)] text-[var(--text-muted)]'}`}>
            {label} ({n})
          </button>
        ))}
      </div>

      {list.length === 0 ? (
        <EmptyState icon={tab === 'inbox' ? Inbox : CheckCircle2} title="No tickets yet"
          hint={tab === 'inbox' ? 'Nothing waiting for you.' : 'Replies from the developer will show up here.'} />
      ) : (
        list.map(t => <Thread key={t.id} ticket={t} onSent={load} isOwnerView={tab === 'inbox'} />)
      )}
    </PageShell>
  );
}
